import React, { useState, useEffect, useCallback } from 'react';
import Tabs from './Tabs';
import Pagination from './Pagination';
import Form from './Form';
import Table from './Table';
import { addEmployee, deleteEmployee, updateEmployee, fetchEmployeesByPage } from './APIService.js';
// import Home from './Home.js';
import AboutUs from './AboutUs.js';
import ContactUs from './ContactUs.js';
// import Login from './Login.js';
// import LoginButton from './LoginButton.js';
import Address from './Address';
import './Employee.css';

const EmployeeDetails = () => {
  const apiUrl = "api";
  const [activeTab, setActiveTab] = useState("home");
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [formData, setFormData] = useState({
    emp_id: '',
    name: '',
    designation: '',
    email: '',
    phone_no: '',
    doj: '',
    department: '',
  });
  const [isEditing, setIsEditing] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [searchQuery, setSearchQuery] = useState("");
  const [, setTotalPages] = useState(1);
  const [count , setCount] = useState(0);
  

  // Memoize the fetchEmployees function using useCallback
  const fetchEmployees = useCallback(async () => {
    try {
      const data = await fetchEmployeesByPage(apiUrl, currentPage, itemsPerPage, "name", "asc");
      if (data && Array.isArray(data.employee_list)) {
        data.employee_list.forEach((employeeObj) => {
          if (employeeObj.address == null) {
            employeeObj.address = "";
          }
        });
        setEmployees(data.employee_list);
        setTotalPages(data.total_pages);
        setCount(data.total_count);
      } else {
        setEmployees([]);
      }
    } catch (error) {
      setEmployees([]);
      console.error("Error fetching employees:", error);
    }
  }, [apiUrl, currentPage, itemsPerPage]);

  useEffect(() => {
    if (activeTab === "employeeManagement") {
      fetchEmployees(); 
    }
  }, [activeTab, fetchEmployees]); 

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleAddClick = () => {
    setFormData({
      emp_id: '',
      name: '',
      designation: '',
      email: '',
      phone_no: '',
      doj: '',
      department: '',
    });
    setIsEditing(false);
    setShowForm(true);
  };

  const handleModifyClick = () => {
    if (!selectedEmployee) return alert("Please select an employee to modify.");
    const employee = employees.find((emp) => emp.id === selectedEmployee);
    setFormData(employee);
    setIsEditing(true);
    setShowForm(true);
  };

  const handleDeleteClick = async () => {
    if (!selectedEmployee) {
      alert("Please select an employee to delete.");
      return;
    }

    try {
      // Delete the employee from the backend
      const result = await deleteEmployee(apiUrl, selectedEmployee);

      if (result.error) {
        alert(result.error);
      } else {
        // Fetch the employee list again after deleting
        fetchEmployees(); // This will update the employee list after deletion
        setSelectedEmployee(null);
      }
    } catch (error) {
      console.error("Error deleting employee:", error);
      alert("There was an error while deleting the employee.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let result;
      const employeeData = { ...formData, address: selectedEmployeeData ? selectedEmployeeData.address : {} };

      if (isEditing) {
        // Update employee
        result = await updateEmployee(apiUrl, employeeData, selectedEmployee);

        if (result.error) {
          alert(result.error);
        } else {
          // Fetch the employee list again after updating
          fetchEmployees(); // This will update the employee list after modifying the data
        }
      } else {
        // Add new employee
        result = await addEmployee(apiUrl, employeeData);

        if (result.error) {
          alert(result.error);
        } else {
          // Fetch the employee list again after adding
          fetchEmployees(); // This will update the employee list after adding a new employee
        }
      }
    } catch (error) {
      console.error("Error handling submit:", error);
      alert("There was an error while submitting the employee data.");
    }
    setShowForm(false);
  };

  const handleLoginClick = () => {
    setActiveTab("Login");
  };

  const filteredEmployees = Array.isArray(employees) ? 
    employees.filter((employee) => {
      const employeeValues = Object.values(employee);
      // Check if any value matches the search query
      return employeeValues.some((value) => 
        value && value.toString().toLowerCase().includes(searchQuery.toLowerCase())
      );
    }) : [];

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentEmployees = filteredEmployees.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredEmployees.length / itemsPerPage);

  const selectedEmployeeData = employees.find(emp => emp.id === selectedEmployee);

  return (
    <div>
      <Tabs activeTab={activeTab} setActiveTab={setActiveTab} />
      {activeTab === "aboutus" && <AboutUs />}
      {activeTab === "contactus" && <ContactUs />}
      {activeTab === "employeeManagement" && (
        <div>
          <h1>Employee Details</h1>
          <input className='search_bar'
            type="text"
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <Table
            employees={currentEmployees}
            selectedEmployee={selectedEmployee}
            setSelectedEmployee={setSelectedEmployee}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
          />
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            handlePageChange={setCurrentPage}
          />
          <div className="pagination-info">
            <p>Total Employees: {count}</p>
          </div>
          <div className="actions-container">
            <button onClick={handleAddClick}>Add Row</button>
            <button onClick={handleModifyClick}>Modify Row</button>
            <button onClick={handleDeleteClick}>Delete Row</button>
          </div>
          {showForm && (
            <Form
              formData={formData}
              handleInputChange={handleInputChange}
              handleSubmit={handleSubmit}
              clearInputs={() => setShowForm(false)}
              isEditing={isEditing} 
            />
          )}
          {selectedEmployeeData && selectedEmployeeData.address ? (
            <div className="address-container">
              <h2>Employee Address</h2>
              <Address addresses={selectedEmployeeData.address["addresses"]} 
                setAddresses={(updatedAddresses) => {
                  const updatedEmployee = { ...selectedEmployeeData, address: { addresses: updatedAddresses }};
                  const updatedEmployees = employees.map((emp) => emp.id === selectedEmployee ? updatedEmployee : emp);
                  setEmployees(updatedEmployees); 
                  updateEmployee(apiUrl, updatedEmployee, selectedEmployee);
                }}
              />
            </div>
          ) : (
            <p>No address available for this employee.</p>
          )}
        </div>
      )}
      <footer className='footer'>
        <section className='footer-top'>
          <div className='footer-column'>
            <ul>
              <li onClick={() => {
                  setActiveTab('aboutus');
                  window.scrollTo(0, 0);
                }}
              >About Us</li>
            </ul>
          </div>
          <div className='footer-column'>
            <ul>
              <li onClick={() => {
                  setActiveTab('contactus');
                  window.scrollTo(0, 0);
                  }}
              >Contact Us</li>
            </ul>
          </div>

          <div className='footer-column'>
            <ul>
              <li onClick={() => {
                  setActiveTab('home');
                  window.scrollTo(0, 0);
                  }}
              >Our Services</li>
            </ul>
          </div>

        </section>
        <div className='fotter-buttom'>
          <div className='social-icons'>
            <a href="https://www.facebook.com/SuyochiRobotics" 
              aria-label="Go to Facebook" 
              className="fa fa-facebook" 
              target="_blank" 
              // rel="noopener noreferrer"
              >
            </a>
            <a href="https://www.linkedin.com/company/suyochirobotics" 
              aria-label="Go to Facebook" 
              className="fa fa-linkedin" 
              target="_blank">
            </a>
            <a href="https://www.instagram.com/suyochirobotics" 
              aria-label="Go to Facebook" 
              className="fa fa-instagram" 
              target="_blank">
            </a>
            <a href="https://www.twitter.com/suyochirobotics" 
              aria-label="Go to Facebook" 
              className="fa fa-twitter" 
              target="_blank">
            </a>
          </div>
          <p> copyright @2025 suyochi.com. All Rights Reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default EmployeeDetails;
